/**
 * Widget de monitorizare orașe — Ventusky/Windy style.
 * Afișează date AQ/meteo pentru principalele orașe din România.
 */
import { CITIES, getDefaultCity, findNearestCity, addCustomCity } from './cities.js';
import { sampleCityValue, sampleAllCities, getCitySparkline, generateDescription } from './cityData.js';
import { getAqiCategory, UNITS, LABELS, valueToColor, THRESHOLDS } from './colormaps.js';
import { initCitySearch } from './citySearch.js';

let selectedCity = null;
let isCollapsed = false;

/**
 * Inițializează widgetul — detectează orașul implicit, construiește DOM.
 * @returns {Promise<object>} Orașul implicit selectat
 */
export async function initCityWidget() {
  selectedCity = await getDefaultCity();

  const toggleBtn = document.getElementById('city-widget-toggle');
  if (toggleBtn) {
    toggleBtn.addEventListener('click', toggleCollapse);
  }

  // Inițializare căutare
  initCitySearch((result) => {
    // Adaugă orașul căutat în registru (dacă nu există)
    const city = addCustomCity(result.name, result.lat, result.lon);
    selectedCity = city;

    // Dispatch event pentru main.js (re-render widget)
    document.dispatchEvent(new CustomEvent('city-selected', {
      detail: { cityId: city.id, city }
    }));
  });

  return selectedCity;
}

/**
 * Actualizează întregul widget cu date din cubul curent.
 * @param {Date[]} [dates] - Array-ul de date (din timesToDates) pentru afișarea orei
 */
export function updateCityWidget(cube, variable, timeIndex, dates) {
  if (!cube || !selectedCity) return;

  // 1. Actualizare hero card
  updateHeroCard(cube, variable, timeIndex, dates);

  // 2. Actualizare lista orașelor
  updateCityList(cube, variable, timeIndex);
}

/**
 * Selectează un oraș din widget.
 */
export function selectCity(cityId) {
  const city = CITIES.find(c => c.id === cityId);
  if (city) {
    selectedCity = city;
  }
}

export function getSelectedCity() {
  return selectedCity;
}

// ── Hero Card ────────────────────────────────────────────────────

function updateHeroCard(cube, variable, timeIndex, dates) {
  const value = sampleCityValue(cube, selectedCity, timeIndex);
  const category = getAqiCategory(value, variable);
  const unit = UNITS[variable] || '';
  const description = generateDescription(value, variable, selectedCity.name);

  // Name
  const nameEl = document.getElementById('city-hero-name');
  if (nameEl) nameEl.textContent = selectedCity.name;

  // Datetime
  const dtEl = document.getElementById('city-hero-datetime');
  if (dtEl && dates && dates[timeIndex]) {
    const d = dates[timeIndex];
    const fmt = new Intl.DateTimeFormat('ro-RO', {
      timeZone: 'Europe/Bucharest',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    dtEl.textContent = fmt.format(d);
  } else if (dtEl) {
    dtEl.textContent = '';
  }

  // Value
  const valueEl = document.getElementById('city-hero-value');
  if (valueEl) {
    if (value != null) {
      valueEl.textContent = value >= 1000
        ? `${(value / 1000).toFixed(1)}k`
        : value.toFixed(1);
    } else {
      valueEl.textContent = '—';
    }
  }

  // Unit
  const unitEl = document.getElementById('city-hero-unit');
  if (unitEl) unitEl.textContent = unit;

  // Badge
  const badgeEl = document.getElementById('city-hero-badge');
  if (badgeEl && category) {
    badgeEl.textContent = category.label;
    badgeEl.style.setProperty('--badge-color', category.color);
  } else if (badgeEl) {
    badgeEl.textContent = '—';
    badgeEl.style.removeProperty('--badge-color');
  }

  // Description
  const descEl = document.getElementById('city-hero-desc');
  if (descEl) descEl.textContent = description;

  // Sparkline
  const canvas = document.getElementById('city-hero-spark');
  if (canvas) {
    const sparkData = getCitySparkline(cube, selectedCity, timeIndex, 12);
    drawSparkline(canvas, sparkData, variable);
  }
}

// ── City List ────────────────────────────────────────────────────

function updateCityList(cube, variable, timeIndex) {
  const container = document.getElementById('city-list');
  if (!container) return;

  const allData = sampleAllCities(cube, variable, timeIndex);

  // Mută orașul selectat la început
  const selectedIdx = allData.findIndex(d => d.city.id === selectedCity.id);
  if (selectedIdx > 0) {
    const [sel] = allData.splice(selectedIdx, 1);
    allData.unshift(sel);
  }

  const unit = UNITS[variable] || '';

  // Generează HTML — refolosește DOM dacă deja există
  const html = allData.map(({ city, value, category }) => {
    const isSelected = city.id === selectedCity.id;
    const valStr = value != null
      ? (value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(1))
      : '—';
    const catColor = category?.color || 'var(--text-muted)';
    const catLabel = category?.label || '—';

    return `
      <button class="city-item${isSelected ? ' selected' : ''}"
              data-city-id="${city.id}"
              id="city-item-${city.id}">
        <span class="city-item-indicator" style="background: ${catColor}"></span>
        <span class="city-item-name">${city.name}</span>
        <span class="city-item-value">${valStr}<small>${unit}</small></span>
        <span class="city-item-cat" style="color: ${catColor}">${catLabel}</span>
      </button>
    `;
  }).join('');

  container.innerHTML = html;

  // Atașează event listeners
  container.querySelectorAll('.city-item').forEach(btn => {
    btn.addEventListener('click', () => {
      const cityId = btn.dataset.cityId;
      selectCity(cityId);
      // Dispatch custom event pentru main.js
      document.dispatchEvent(new CustomEvent('city-selected', {
        detail: { cityId, city: selectedCity }
      }));
    });
  });
}

// ── Sparkline ────────────────────────────────────────────────────

function drawSparkline(canvas, data, variable) {
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;

  // Use CSS dimensions as source of truth (prevents DPI compounding)
  const W = parseInt(canvas.style.width) || canvas.getAttribute('width') || 260;
  const H = parseInt(canvas.style.height) || canvas.getAttribute('height') || 48;

  canvas.width = W * dpr;
  canvas.height = H * dpr;
  canvas.style.width = `${W}px`;
  canvas.style.height = `${H}px`;
  ctx.scale(dpr, dpr);

  ctx.clearRect(0, 0, W, H);

  const filtered = data.filter(v => v != null);
  if (filtered.length < 2) return;

  // Use data range for better visual resolution
  const dataMin = Math.min(...filtered);
  const dataMax = Math.max(...filtered);
  const margin = (dataMax - dataMin) * 0.15 || 1;
  const minVal = dataMin - margin;
  const maxVal = dataMax + margin;
  const range = maxVal - minVal || 1;

  const padX = 4;
  const padY = 6;
  const plotW = W - padX * 2;
  const plotH = H - padY * 2;

  // Calculează punctele
  const points = data.map((v, i) => {
    const x = padX + (i / (data.length - 1)) * plotW;
    const yNorm = v != null ? (v - minVal) / range : 0.5;
    const y = padY + plotH - yNorm * plotH;
    return { x, y, v };
  });

  // Culoare: folosim accent cyan pentru AQ (contrastează bine pe fundal închis)
  const isAQ = ['pm25', 'pm10', 'no2', 'o3', 'so2', 'co'].includes(variable);
  const midVal = filtered[Math.floor(filtered.length / 2)];
  let r, g, b;

  if (isAQ) {
    // Cyan accent — vizibil pe fundal închis indiferent de categorie
    [r, g, b] = [0, 200, 220];
  } else {
    [r, g, b] = valueToColor(midVal, variable);
  }

  // Fill gradient sub linie
  const gradient = ctx.createLinearGradient(0, padY, 0, H);
  gradient.addColorStop(0, `rgba(${r},${g},${b}, 0.25)`);
  gradient.addColorStop(1, `rgba(${r},${g},${b}, 0.01)`);

  ctx.beginPath();
  let started = false;
  for (const p of points) {
    if (p.v == null) continue;
    if (!started) {
      ctx.moveTo(p.x, p.y);
      started = true;
    } else {
      ctx.lineTo(p.x, p.y);
    }
  }
  // Închide path-ul pentru fill
  const lastValid = points.filter(p => p.v != null).pop();
  const firstValid = points.find(p => p.v != null);
  if (lastValid && firstValid) {
    ctx.lineTo(lastValid.x, padY + plotH);
    ctx.lineTo(firstValid.x, padY + plotH);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();
  }

  // Linia sparkline
  ctx.beginPath();
  started = false;
  for (const p of points) {
    if (p.v == null) continue;
    if (!started) {
      ctx.moveTo(p.x, p.y);
      started = true;
    } else {
      ctx.lineTo(p.x, p.y);
    }
  }
  ctx.strokeStyle = `rgba(${r},${g},${b}, 0.85)`;
  ctx.lineWidth = 1.5;
  ctx.lineJoin = 'round';
  ctx.stroke();

  // Punct final (valoare curentă)
  if (lastValid) {
    ctx.beginPath();
    ctx.arc(lastValid.x, lastValid.y, 3, 0, Math.PI * 2);
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

// ── Toggle ───────────────────────────────────────────────────────

function toggleCollapse() {
  const body = document.getElementById('city-widget-body');
  const btn = document.getElementById('city-widget-toggle');
  const widget = document.getElementById('city-widget');

  if (!body || !widget) return;

  isCollapsed = !isCollapsed;
  widget.classList.toggle('collapsed', isCollapsed);

  if (btn) {
    const svg = btn.querySelector('svg');
    if (svg) {
      svg.style.transform = isCollapsed ? 'rotate(180deg)' : '';
    }
  }
}
